import { Badge } from "@/components/shadcn/badge";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/shadcn/card";
import type { ClassLevel } from "@prisma/client";
import { Award, User } from "lucide-react";
import { useTranslations } from "next-intl";
import { getClassLevelLabelKey } from "../utils/class-level-utils";
import { getClassCapacityText } from "../utils/get-class-capacity-text";
import StudentLeaveClassButton from "./student-leave-class-button";

type Instructor = {
    name?: string | null;
    surname?: string | null;
    nickName?: string | null;
};

export type ClassCardData = {
    id: number;
    name: string;
    description: string | null;
    level: ClassLevel;
    capacity?: number;
    memberCount?: number;
    instructor?: Instructor;
};

export default function ClassCard({
    classData,
    className,
    onClick,
    isStudent = false,
    onLeaveClass,
}: {
    classData: ClassCardData;
    className?: string;
    onClick?: () => void;
    isStudent?: boolean;
    onLeaveClass?: () => void;
}) {
    return (
        <Card className={className} onClick={!isStudent ? onClick : undefined}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="cursor-pointer text-lg">
                        {classData.name}
                    </CardTitle>
                    {classData.capacity !== undefined &&
                        classData.memberCount !== undefined && (
                            <CapacityBadge
                                capacity={classData.capacity}
                                memberCount={classData.memberCount}
                            />
                        )}
                </div>
            </CardHeader>

            <CardContent className="flex flex-col gap-2">
                <LevelBadge level={classData.level} />
                {isStudent && classData.instructor && (
                    <InstructorName instructor={classData.instructor} />
                )}
                {classData.description && (
                    <p className="line-clamp-2 text-sm text-gray-600">
                        {classData.description}
                    </p>
                )}
            </CardContent>

            {isStudent && (
                <CardFooter className="pt-0">
                    <StudentLeaveClassButton
                        classId={classData.id}
                        className={classData.name}
                        onClassLeft={onLeaveClass}
                    />
                </CardFooter>
            )}
        </Card>
    );
}

function CapacityBadge({
    capacity,
    memberCount,
}: {
    capacity: number;
    memberCount: number;
}) {
    return (
        <Badge className="bg-success text-success-foreground flex items-center gap-1 rounded-full">
            <User size={14} className="font-bold" />
            {getClassCapacityText(capacity, memberCount)}
        </Badge>
    );
}

function LevelBadge({ level }: { level: ClassLevel }) {
    const t = useTranslations("classes");
    return (
        <div className="flex items-center gap-2">
            <Award size={14} className="font-bold" />
            <span className="text-sm text-gray-500">
                {t(getClassLevelLabelKey(level))}
            </span>
        </div>
    );
}

function InstructorName({ instructor }: { instructor: Instructor }) {
    const t = useTranslations("classes");
    const displayName =
        instructor.name || instructor.surname
            ? `${instructor.name || ""} ${instructor.surname || ""}`.trim()
            : instructor.nickName || "";

    if (!displayName) return null;

    return (
        <div className="flex items-center gap-2">
            <User size={14} className="font-bold" />
            <span className="text-sm text-gray-500">
                {t("trainerLabel")} {displayName}
            </span>
        </div>
    );
}
